import type {
  ExpertSubscription,
  ListExpertSubscriptionInvoicesItem,
} from "@shopexperts/legacy-api";

export type AdminBillingProviderSubscriptionStatus =
  | "checkout_pending"
  | "incomplete"
  | "incomplete_expired"
  | "trialing"
  | "active"
  | "past_due"
  | "unpaid"
  | "paused"
  | "canceled";

export interface AdminProviderSubscriptionRecord {
  id: number;
  provider: string;
  mode: "test" | "live";
  provider_subscription_id: string;
  status: AdminBillingProviderSubscriptionStatus;
  cancel_at_period_end: boolean;
  current_period_starts_at: string | null;
  current_period_ends_at: string | null;
  trial_ends_at: string | null;
  cancel_at: string | null;
  canceled_at: string | null;
  provider_state_updated_at?: string | null;
  billing_option?: {
    id: number;
    label: string;
    interval_unit: "week" | "month" | "year";
    interval_count: number;
    amount_cents: number;
    currency: string;
  } | null;
  invitation?: { id: number; status?: string } | null;
  invitation_id?: number | null;
}

export type AdminExpertSubscriptionRecord = ExpertSubscription & {
  is_managed: boolean;
  provider_subscription: AdminProviderSubscriptionRecord | null;
};

export type AdminExpertInvoiceRecord = ListExpertSubscriptionInvoicesItem & {
  provider?: string | null;
  provider_status?: string | null;
  service_period_starts_at?: string | null;
  service_period_ends_at?: string | null;
};

export type AdminSubscriptionManagement = "all" | "managed" | "manual";

export const adminSubscriptionManagementOptions = [
  { label: "All", value: "all" },
  { label: "Stripe-managed", value: "managed" },
  { label: "Manual", value: "manual" },
] as const;

export interface ProviderStatusPresentation {
  label: string;
  variant: "default" | "highlight" | "success" | "warn" | "neutral";
  detail: string;
}

export function getProviderStatusPresentation(
  status: AdminBillingProviderSubscriptionStatus,
  cancelAtPeriodEnd = false,
): ProviderStatusPresentation {
  if (cancelAtPeriodEnd && ["trialing", "active", "past_due"].includes(status)) {
    return {
      label: "Ending",
      variant: "warn",
      detail: "Cancellation is scheduled in Stripe at the end of the current period.",
    };
  }

  const states: Record<AdminBillingProviderSubscriptionStatus, ProviderStatusPresentation> = {
    checkout_pending: {
      label: "Checkout pending",
      variant: "neutral",
      detail: "The expert has not completed Stripe Checkout yet.",
    },
    incomplete: {
      label: "Incomplete",
      variant: "warn",
      detail: "Stripe is waiting for the subscription's first successful payment.",
    },
    incomplete_expired: {
      label: "Checkout expired",
      variant: "neutral",
      detail: "The incomplete Stripe subscription can no longer be completed.",
    },
    trialing: {
      label: "Trialing",
      variant: "success",
      detail: "Stripe will begin charging when the preserved paid period ends.",
    },
    active: {
      label: "Active",
      variant: "success",
      detail: "Stripe is managing renewals for this subscription.",
    },
    past_due: {
      label: "Past due",
      variant: "warn",
      detail: "Stripe is retrying payment under its configured recovery policy.",
    },
    unpaid: {
      label: "Unpaid",
      variant: "highlight",
      detail: "Stripe exhausted payment recovery; no new paid service period was added.",
    },
    paused: {
      label: "Paused",
      variant: "warn",
      detail: "Stripe paused collection; the provider relationship remains intact.",
    },
    canceled: {
      label: "Canceled",
      variant: "neutral",
      detail: "Stripe ended this subscription and the local record mirrors that outcome.",
    },
  };

  return states[status]!;
}

export function isManagedSubscription(subscription: AdminExpertSubscriptionRecord | null | undefined) {
  return subscription?.is_managed === true;
}

export function getSubscriptionAccentTone(
  subscription: AdminExpertSubscriptionRecord,
  manualTone: string,
) {
  const provider = subscription.provider_subscription;
  if (!subscription.is_managed) return manualTone;
  if (!provider) return "inactive";
  if (provider.cancel_at_period_end) return "ending";
  if (["trialing", "active"].includes(provider.status)) return "active";
  if (["incomplete", "past_due", "unpaid", "paused"].includes(provider.status)) return "ending";
  if (["incomplete_expired", "canceled"].includes(provider.status)) return "expired";
  return "inactive";
}

export function canCancelLocally(subscription: AdminExpertSubscriptionRecord | null | undefined) {
  return Boolean(subscription) && !isManagedSubscription(subscription);
}

export function isManagedInvoice(invoice: AdminExpertInvoiceRecord) {
  return invoice.invoice_type === "managed" || Boolean(invoice.provider);
}

export function canMutateInvoiceLocally(invoice: AdminExpertInvoiceRecord) {
  return !isManagedInvoice(invoice) && invoice.invoice_type === "external";
}

export function getLinkedInvitationId(subscription: AdminExpertSubscriptionRecord | null | undefined) {
  const provider = subscription?.provider_subscription;
  return provider?.invitation?.id ?? provider?.invitation_id ?? null;
}

export function describeRecurrence(intervalUnit: string, intervalCount: number) {
  if (intervalCount === 1) return `every ${intervalUnit}`;
  return `every ${intervalCount} ${intervalUnit}s`;
}
